import { SpinnerSize } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPActionButton, IPSeparator, IPSpinner, IPStack, IPText, IPToggle } from 'baseComponents';
import { CopilotActionButton } from 'components/Copilot/CopilotButtons';
import GenericModal from 'components/Dialogs/GenericModal';
import AISuggestionMessage from 'components/Notification/AISuggestionMessage';
import SuccesMessage from 'components/Notification/SuccesMessage';
import WarningMessage from 'components/Notification/WarningMessage';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalStackTokensGapMedium, globalStackTokensGapSmall, saveIcon } from 'globalStyles';
import {
  ContentBlockKind,
  CopilotConversationItem,
  CopilotManagerPromptType,
} from 'models/copilot/copilotConversation';
import { CopilotProcessingActivity } from 'models/copilot/copilotProcessingActivity';
import ProcessingActivity, { ComplianceIssue, PrivacyFramework } from 'models/processingActivity';
import { AIEnabled } from 'models/setting';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCopilotProcessingActivityCompliance } from 'services/Api/copilotService';
import { apiGetProcessingActivityCompliance } from 'services/Api/processingActivityService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';

interface IComplianceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
  processingActivity: ProcessingActivity | undefined;
  setHasLoaded: (loaded: boolean) => void;
  hasChanged: boolean;
  modelUpToDate: boolean;
  setModelUpToDate: (upToDate: boolean) => void;
}

export const ComplianceModal = (props: IComplianceModalProps) => {
  const { t } = useTranslation(['translation', 'processingActivity', 'processingActivities']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [AIIsLoading, setAIIsLoading] = useState(false);
  const [complianceIssues, setComplianceIssues] = useState<string[]>([]);
  const [copilotProcessingActivity, setCopilotProcessingActivity] = useState<CopilotProcessingActivity | undefined>(
    undefined,
  );
  const [copilotComplianceIssues, setCopilotComplianceIssues] = useState<string[] | undefined>(undefined);
  const [showAISuggestions, setShowAISuggestions] = useState<boolean>(
    getLocalStorageData(appContext, LocalStorageKeys.ProcessingActivityComplianceCheckAI) === 'true',
  );
  const [hasAIFeature] = useState<boolean>(
    hasUserFeature(appContext, FeatureTypes.AIAssistant) &&
      (appContext.globalDataCache.settings.get(AIEnabled) as boolean) === true,
  );
  const privacyFramework =
    (appContext.globalDataCache.settings.get('PrivacyFramework') as PrivacyFramework) ?? PrivacyFramework.GDPR;

  useEffect(() => {
    if (props.isOpen && props.processingActivity) {
      if (!hasLoaded) {
        refresh(false);
        setHasLoaded(true);
      } else if (!props.modelUpToDate) {
        refresh(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.processingActivity]);

  useEffect(() => {
    setHasLoaded(false);
    setComplianceIssues([]);
    setCopilotProcessingActivity(undefined);
    setCopilotComplianceIssues(undefined);
  }, [props.processingActivity?.processingActivityId]);

  const loadData = async (processingActivity: ProcessingActivity | undefined) => {
    if (!processingActivity) return;

    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const issues: ComplianceIssue[] = await apiGetProcessingActivityCompliance(
        accessToken,
        processingActivity.processingActivityId,
        privacyFramework,
      );

      const result = issues
        .map((issue) => processingActivity.getComplianceIssueText(issue, t, privacyFramework))
        .filter((issue) => issue !== '');

      props.setHasLoaded(true);
      setComplianceIssues(result);
    } catch (error) {
      appContext.setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAISuggestions = async () => {
    if (!props.processingActivity) return;

    try {
      setAIIsLoading(true);

      const _copilotProcessingActivity = copilotProcessingActivity
        ? copilotProcessingActivity
        : new CopilotProcessingActivity();
      _copilotProcessingActivity.processingActivityId = props.processingActivity.processingActivityId;

      _copilotProcessingActivity.conversation.request = new CopilotConversationItem();
      _copilotProcessingActivity.conversation.request.promptType = CopilotManagerPromptType.Context;

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newCopilotProcessingActivity = await apiCopilotProcessingActivityCompliance(
        accessToken,
        _copilotProcessingActivity,
        privacyFramework,
      );

      setCopilotProcessingActivity(newCopilotProcessingActivity);
      if (newCopilotProcessingActivity && newCopilotProcessingActivity.conversation.thread.length > 1) {
        const responseItem =
          newCopilotProcessingActivity.conversation.thread[newCopilotProcessingActivity.conversation.thread.length - 1];
        const table = responseItem.contentBlocks?.find((block) => block.kind === ContentBlockKind.DataTable)?.dataTable;
        const _copilotComplianceIssues = (table?.rows ?? [])
          .map((row) => String(row.issue ?? ''))
          .filter((issue) => issue.length > 0);
        setCopilotComplianceIssues(_copilotComplianceIssues);
      }
    } catch (error) {
      appContext.setError(error);
    } finally {
      setAIIsLoading(false);
    }
  };

  const refresh = async (saveFirst: boolean) => {
    if (!props.processingActivity) return;
    try {
      setIsLoading(true);
      if (saveFirst) await props.onSave();
      await loadData(props.processingActivity);
    } catch (error) {
      appContext.setError(error);
    } finally {
      setIsLoading(false);
      props.setModelUpToDate(true);
    }
    setCopilotComplianceIssues(undefined);
    if (showAISuggestions && hasAIFeature) {
      try {
        setAIIsLoading(true);
        await getAISuggestions();
      } catch (error) {
        appContext.setError(error);
      } finally {
        setAIIsLoading(false);
      }
    }
  };

  const ToggleAI = async () => {
    setShowAISuggestions(!showAISuggestions);
    setLocalStorageData(
      appContext,
      LocalStorageKeys.ProcessingActivityComplianceCheckAI,
      (!showAISuggestions).toString(),
    );
    if (copilotComplianceIssues === undefined && !showAISuggestions) {
      await getAISuggestions();
    }
  };

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      keepOpenAfterSave={true}
      title={t('processingActivity:ComplianceModal.Title')}
      subTitle={t('processingActivity:ComplianceModal.SubTitle')}
      height={700}
      width={1000}
      maxWidth={'min(90vw, 1000px)'}
      maxHeight={'90vh'}
      minHeight={300}
      minWidth={300}
    >
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        <IPStack horizontalAlign="start" verticalAlign="start" horizontal tokens={globalStackTokensGapMedium}>
          {hasAIFeature && (
            <IPStack>
              <IPStack.Item>
                <CopilotActionButton
                  onClick={ToggleAI}
                  text={t('processingActivity:ComplianceModal.Button.AIAssist')}
                  showSpinner={AIIsLoading}
                  disabled={AIIsLoading || isLoading}
                />
              </IPStack.Item>
              <IPStack.Item styles={{ root: { paddingLeft: 8 } }}>
                <IPToggle
                  disabled={AIIsLoading || isLoading}
                  onClick={ToggleAI}
                  checked={showAISuggestions}
                  onText={t('translation:General.Toggle.On')}
                  offText={t('translation:General.Toggle.Off')}
                />
              </IPStack.Item>
            </IPStack>
          )}
          <IPStack.Item>
            <IPActionButton
              text={t('processingActivity:ComplianceModal.Button.SaveAndRecheck')}
              iconProps={saveIcon}
              disabled={isLoading || AIIsLoading} //enable even when no changes because controls/risks/processes may have changed
              onClick={() => refresh(props.hasChanged)}
            />
          </IPStack.Item>
        </IPStack>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        <ScrollableStackItem>
          {isLoading && (
            <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
              <IPStack.Item>
                <IPSpinner size={SpinnerSize.large} />
              </IPStack.Item>
            </IPStack>
          )}
          {!isLoading && (
            <IPStack tokens={globalStackTokensGapMedium}>
              {complianceIssues.length > 0 && (
                <IPStack tokens={globalStackTokensGapSmall}>
                  <IPStack.Item>
                    <IPText>{t('processingActivity:ComplianceModal.IssuesFound')}</IPText>
                  </IPStack.Item>
                  {complianceIssues.map((issue, index) => (
                    <IPStack.Item key={index}>
                      <WarningMessage message={issue} />
                    </IPStack.Item>
                  ))}
                </IPStack>
              )}
              {complianceIssues.length === 0 && (
                <IPStack.Item>
                  <SuccesMessage message={t('processingActivity:ComplianceModal.NoIssues')} />
                </IPStack.Item>
              )}
              {copilotComplianceIssues && showAISuggestions && copilotComplianceIssues.length > 0 && (
                <IPStack tokens={globalStackTokensGapSmall}>
                  <IPStack.Item>
                    <IPText>{t('processingActivity:ComplianceModal.AISuggestionsFound')}</IPText>
                  </IPStack.Item>
                  {copilotComplianceIssues.map((suggestion, index) => (
                    <IPStack.Item key={index}>
                      <AISuggestionMessage message={suggestion} />
                    </IPStack.Item>
                  ))}
                </IPStack>
              )}
              {copilotComplianceIssues && showAISuggestions && copilotComplianceIssues.length === 0 && (
                <IPStack.Item>
                  <SuccesMessage message={t('processingActivity:ComplianceModal.NoAISuggestions')} />
                </IPStack.Item>
              )}
            </IPStack>
          )}
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};
